import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Star, User, Calendar, MessageSquare } from 'lucide-react';
import { trainerReviews_ } from '../../../slices/reportSlice';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import { decrypt } from '../../../common/enc_dec';
import { useModuleSettings } from '../../../components/moduleSettingsContext/ModuleSettingsContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const ReviewsList = () => {
    const { trainerReviews } = useSelector((state) => state.report);
    const dispatch = useDispatch();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const _id = decrypt(queryParams.get("id"));
    const { id } = location?.state || {};
    const trainerId = id || _id;

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [accumulatedData, setAccumulatedData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(10);
    const { filter, setFilter } = useModuleSettings();
    const [isExporting, setIsExporting] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Initial data load
    useEffect(() => {
        fetchReviewData(1);
    }, [dispatch, trainerId, filter]);

    // Update accumulated data when trainerReviews changes
    useEffect(() => {
        if (trainerReviews?.data) {
            // For page 1, replace the data
            if (page === 1) {
                setAccumulatedData(trainerReviews.data);
            } else {
                // For subsequent pages, append the data
                setAccumulatedData(prevData => {
                    // Create a new array with unique items by id
                    const combinedData = [...prevData, ...trainerReviews.data];
                    const uniqueData = Array.from(
                        new Map(combinedData.map(item => [item.id, item])).values()
                    );
                    return uniqueData;
                });
            }

            // Update total count for pagination checks
            if (trainerReviews?.paging?.total) {
                setTotalCount(trainerReviews.paging.total);
            }

            // Check if we've loaded all items
            if (accumulatedData.length >= trainerReviews.paging.total) {
                setHasMore(false);
            } else {
                setHasMore(true);
            }

            setIsLoading(false);
        }
    }, [trainerReviews, page]);

    // Function to fetch review data with filter parameters
    const fetchReviewData = (pageToken) => {
        if (isLoading) return;

        setIsLoading(true);

        const params = {
            page: pageToken,
            limit: limit,
            trainer_id: trainerId
        };

        // Add filter parameters if selected
        if (filter) {
            params.period = filter;

            // Add date range for custom filter
            if (filter === 'custom' && startDate && endDate) {
                params.startDate = moment(startDate).format("YYYY-MM-DD");
                params.endDate = moment(endDate).format("YYYY-MM-DD");
            }
        }

        dispatch(trainerReviews_(params))
            .then(() => {
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setHasMore(false);
            });
    };

    // Function to load more data when scrolling
    const fetchMoreData = () => {
        if (!isLoading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchReviewData(nextPage);
        }
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setPage(1); // Reset to first page when filter changes

        // Clear custom dates when switching away from custom filter
        if (event.target.value !== 'custom') {
            setStartDate(null);
            setEndDate(null);
        }
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate && moment(startDate).isBefore(endDate)) {
            setPage(1); // Reset to first page

            dispatch(
                trainerReviews_({
                    trainer_id: trainerId,
                    period: "custom",
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    page: 1,
                    limit: limit
                })
            );
        } else {
            Swal.fire({
                title: 'Invalid Dates',
                text: 'Please select valid start and end dates.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    // Export reviews data to Excel
    const handleExportData = useCallback(async () => {
        try {
            setIsExporting(true);

            // Fetch all data for export
            const params = {
                trainer_id: trainerId,
                period: filter,
                page: 1,
                is_report: 1 // Flag to get all data for report
            };

            // Add custom date range if applicable
            if (filter === 'custom' && startDate && endDate) {
                params.startDate = moment(startDate).format("YYYY-MM-DD");
                params.endDate = moment(endDate).format("YYYY-MM-DD");
            }

            await dispatch(trainerReviews_(params))
                .then((response) => {
                    if (response.payload && response.payload.data) {
                        // Map the data to the format we want for Excel
                        const exportData = response.payload.data.map((review, index) => ({
                            "Sr No": index + 1,
                            "Student Name": review.user_trainer_review?.full_name || 'Anonymous User',
                            "Rating": review.rating || "NA",
                            "Review": review.review || "NA",
                            "Review Date": review.review_date ?
                                moment(review.review_date).format("DD-MMM-YYYY") : "NA"
                        }));

                        // Create summary data
                        const totalReviews = response.payload.paging?.total || exportData.length;
                        const avgRating = response.payload.data[0]?.trainer_review?.avg_review || "0.0";

                        const summaryData = [
                            {
                                "Report Details": "Trainer Reviews Summary",
                                "Value": ""
                            },
                            {
                                "Report Details": "Total Reviews",
                                "Value": totalReviews
                            },
                            {
                                "Report Details": "Average Rating",
                                "Value": avgRating
                            },
                            {
                                "Report Details": "Export Date",
                                "Value": moment().format("DD-MMM-YYYY HH:mm:ss")
                            }
                        ];

                        // Create workbook and worksheets
                        const workbook = XLSX.utils.book_new();

                        // Add summary sheet
                        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
                        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

                        // Add reviews data sheet
                        const reviewsSheet = XLSX.utils.json_to_sheet(exportData);
                        XLSX.utils.book_append_sheet(workbook, reviewsSheet, "Review Data");

                        // Save the file
                        const fileName = `Trainer_Reviews_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

                        Swal.fire({
                            icon: "success",
                            title: "Export Successful",
                            text: `Data exported to ${fileName}`,
                        });

                        // Refresh the original view
                        fetchReviewData(1);
                    } else {
                        throw new Error("No data available for export");
                    }
                })
                .finally(() => {
                    setIsExporting(false);
                });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.message || "Unable to export review data",
            });
            setIsExporting(false);
        }
    }, [dispatch, trainerId, filter, startDate, endDate]);

    const formatDate = (date) => new Date(date).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });

    const getRatingStars = (rating) => {
        const stars = [];
        const ratingValue = parseFloat(rating);

        for (let i = 1; i <= 5; i++) {
            if (i <= ratingValue) {
                stars.push(<Star key={i} fill="#ffc107" color="#ffc107" size={16} />);
            } else if (i - 0.5 <= ratingValue) {
                stars.push(<Star key={i} fill="#ffc107" color="#ffc107" size={16} style={{ clipPath: 'inset(0 50% 0 0)' }} />);
            } else {
                stars.push(<Star key={i} color="#e0e0e0" size={16} />);
            }
        }
        return stars;
    };

    // Calculate average rating from the first review's trainer_review.avg_review or default to 0
    const averageRating = accumulatedData.length > 0 &&
        accumulatedData[0].trainer_review &&
        accumulatedData[0].trainer_review.avg_review
        ? accumulatedData[0].trainer_review.avg_review : '0.0';

    // Get total reviews count from paging info or default to current data length
    const totalReviews = trainerReviews?.paging?.total;

    // Check if there are any reviews
    const hasReviews = accumulatedData && accumulatedData.length > 0;

    return (
        <div>
            <h5 className="fw-semibold border-bottom pb-2 mb-3">💬 Student Reviews</h5>

            <div className="row mb-4">
                {/* Rating Information */}
                <div className="col-md-6">
                    {hasReviews ? (
                        <div className="d-flex align-items-center">
                            <div className="bg-warning text-white rounded p-2 me-3">
                                <Star size={24} />
                            </div>
                            <div>
                                <h2 className="mb-0 fw-bold">{averageRating}</h2>
                                <p className="text-muted mb-0">{totalReviews} reviews</p>
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center">
                            <div className="bg-light text-muted rounded p-2 me-3">
                                <Star size={24} />
                            </div>
                            <div>
                                <h2 className="mb-0 fw-bold text-muted">0.0</h2>
                                <p className="text-muted mb-0">No reviews yet</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Filter and Export Controls - Always displayed */}
                <div className="col-md-6">
                    <div className="d-flex justify-content-end align-items-center gap-3">
                        {/* Filter Dropdown */}
                        <label className="form-label">Time Period</label>
                        <div className="filter-container">
                            <select
                                className="form-select"
                                onChange={handleFilterChange}
                                value={filter}
                                style={{ minWidth: "120px" }}
                            >
                                <option value="">All</option>
                                <option value="day">Today</option>
                                <option value="week">Week</option>
                                <option value="month">Month</option>
                                <option value="year">Year</option>
                                <option value="custom">Custom Range</option>
                            </select>
                        </div>

                        {/* Export Button - Disabled when no reviews */}
                        <button
                            onClick={handleExportData}
                            className="btn btn-outline-warning d-flex align-items-center gap-2 text-black"
                            disabled={isExporting || !hasReviews}
                        >
                            {isExporting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    <span>Exporting...</span>
                                </>
                            ) : (
                                <>
                                    <img
                                        src="/assets/images/download.svg"
                                        alt="Download"
                                        style={{ width: "16px", height: "16px" }}
                                        className="text-warning"
                                    />
                                    <span>Generate Report</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Custom Date Range (only shown when custom filter is selected) */}
            {filter === "custom" && (
                <div className="row mb-4">
                    <div className="col-md-10 offset-md-1">
                        <div className="card border-0 shadow-sm">
                            <div className="card-body">
                                <div className="row g-2">
                                    <div className="col-md-5">
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => setStartDate(date)}
                                            selectsStart
                                            startDate={startDate}
                                            endDate={endDate}
                                            placeholderText="Start Date"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-md-5">
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date) => setEndDate(date)}
                                            selectsEnd
                                            startDate={startDate}
                                            endDate={endDate}
                                            minDate={startDate}
                                            placeholderText="End Date"
                                            className="form-control"
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <button className="btn btn-primary w-100" onClick={handleCustomDateChange}>
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reviews List or Empty State Message */}
            {!hasReviews && !isLoading ? (
                <div className="card border-0 shadow-sm p-4 text-center">
                    <MessageSquare size={40} className="text-muted mb-2 mx-auto" />
                    <p className="text-muted mb-0">No reviews available for this trainer.</p>
                    <p className="text-muted small">Use the filter options above to change your search criteria.</p>
                </div>
            ) : (
                <InfiniteScroll
                    dataLength={accumulatedData.length}
                    next={fetchMoreData}
                    hasMore={hasMore}
                    loader={<div className="text-center my-3"><h4>Loading...</h4></div>}
                    endMessage={
                        <p style={{ textAlign: 'center' }}>
                            <b>You've seen all reviews</b>
                        </p>
                    }
                    scrollThreshold={0.9}
                >
                    {accumulatedData.map((review, index) => (
                        <div key={review.id || index} className="card mb-3 border-0 shadow-sm">
                            <div className="card-body">
                                <div className="d-flex justify-content-between mb-2">
                                    <div className="d-flex align-items-center mb-2">
                                        {review.user_trainer_review?.image ? (
                                            <img
                                                src={review.user_trainer_review.image}
                                                alt={review.user_trainer_review?.full_name}
                                                className="rounded-circle me-2"
                                                width="40"
                                                height="40"
                                            />
                                        ) : (
                                            <div className="bg-light rounded-circle d-flex justify-content-center align-items-center me-2"
                                                style={{ width: '40px', height: '40px' }}>
                                                <User size={20} className="text-primary" />
                                            </div>
                                        )}
                                        <div>
                                            <h6 className="mb-0 fw-semibold">{review.user_trainer_review?.full_name || 'Anonymous User'}</h6>
                                            <div className="d-flex align-items-center">
                                                <div className="me-2">
                                                    {getRatingStars(review.rating)}
                                                </div>
                                                <small className="text-muted">{review.rating}</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-muted small d-flex align-items-center">
                                        <Calendar size={14} className="me-1" />
                                        {formatDate(review.review_date || review.createdAt)}
                                    </div>
                                </div>

                                {review.review && (
                                    <div className="mt-2">
                                        <p className="mb-1">{review.review}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            )}
        </div>
    );
};

export default ReviewsList;